import { ActionIcon, Alert, Anchor, Button, CopyButton, Group, Stack, Switch, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy, IconExternalLink, IconEye, IconQrcode, IconWorld } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { sporocilo } from '../api/napaka';
import { api, izvedi, type Shema } from '../api/odjemalec';
import { odpriPdf } from '../api/pdf';
import { casIzMs, datum, stevilo } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { PomocIkona } from '../pomoc/PomocIkona';
import { stanjePovprasevanja } from '../povprasevanja/povprasevanja';
import { Oznaka } from '../strani/PregledRacunov';

type Podrobnosti = Shema['VoziloPodrobnosti'];

/**
 * Javna ponudba pri vozilu: ali je vozilo na spletu (stikalo »Prikaži na spletu«, med urejanjem vozila zaklenjeno),
 * javna povezava (odpri, kopiraj), ogledi javne strani (samo tu, ne na javni strani) in oglas za okno vozila s kodo QR
 * (PDF). Ko vozilo ni javno, je razlog napisan.
 */
export function JavnaPonudbaVozila({ podrobnosti, urejanje }: { podrobnosti: Podrobnosti; urejanje: boolean }) {
  const odjemalec = useQueryClient();
  const v = podrobnosti.vozilo!;
  const [dela, setDela] = useState<'javno' | 'pdf' | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const povezava = podrobnosti.javnaPovezava;
  const ni = podrobnosti.javnoNi;
  const niVklopljena = !povezava || (ni?.startsWith('Javna ponudba ni vklopljena') ?? false);

  const prikazi = async (javno: boolean) => {
    setNapaka(null);
    setDela('javno');
    try {
      await izvedi(api.PUT('/api/vozila/{id}/javno', { params: { path: { id: v.id! } }, body: { vrednost: javno } }));
      await odjemalec.invalidateQueries({ queryKey: ['vozilo', v.id] });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  const oglas = async () => {
    setNapaka(null);
    setDela('pdf');
    try {
      await odpriPdf(api.GET('/api/vozila/{id}/oglas/pdf', { params: { path: { id: v.id! } }, parseAs: 'blob' }));
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };

  return (
    <Kartica naslov="Javna ponudba" dejanja={<PomocIkona clanek="javna-ponudba" />}>
      {niVklopljena ? (
        <Stack gap="xs">
          <Text size="sm">
            Vozila lahko objavite na spletu – s povezavo do vsakega vozila, celotno zalogo in kodo QR za okno vozila,
            prek katere kupci pošljejo povpraševanje.
          </Text>
          <Button component={Link} to="/nastavitve/javna-ponudba" variant="light" leftSection={<IconWorld size={18} />}>
            Vklopi javno ponudbo
          </Button>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Switch
            label="Prikaži na spletu"
            description={urejanje ? 'Najprej shranite ali prekličite urejanje.' : undefined}
            checked={v.javno !== false}
            disabled={urejanje || dela === 'javno'}
            onChange={(e) => void prikazi(e.currentTarget.checked)}
          />
          <Text size="sm" c={ni ? 'dimmed' : undefined} fw={ni ? undefined : 600}>
            {ni ?? 'Vozilo je objavljeno na spletu.'}
          </Text>
          <Ogledi ogledi={podrobnosti.ogledi} />
          {povezava && (
            <Group gap={6} wrap="nowrap">
              <Anchor href={povezava} target="_blank" rel="noopener" size="sm" truncate style={{ minWidth: 0 }}>
                {povezava}
              </Anchor>
              <CopyButton value={povezava}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Kopirano' : 'Kopiraj povezavo'}>
                    <ActionIcon variant="subtle" onClick={copy} aria-label="Kopiraj povezavo">
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
              <ActionIcon
                component="a"
                href={povezava}
                target="_blank"
                rel="noopener"
                variant="subtle"
                aria-label="Odpri javno stran"
              >
                <IconExternalLink size={16} />
              </ActionIcon>
            </Group>
          )}
          <Button
            variant="default"
            leftSection={<IconQrcode size={18} />}
            onClick={() => void oglas()}
            loading={dela === 'pdf'}
            disabled={!!ni}
          >
            Oglas s kodo QR (PDF)
          </Button>
          {ni && (
            <Text size="xs" c="dimmed" mt={-6}>
              Oglas je mogoč, ko je vozilo objavljeno na spletu.
            </Text>
          )}
        </Stack>
      )}
      {napaka && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {napaka}
        </Alert>
      )}
    </Kartica>
  );
}

/** Ogledi javne strani vozila, kot jih šteje strežnik (osvežitev istega obiskovalca v 30 minutah se ne šteje). */
function Ogledi({ ogledi }: { ogledi?: Shema['OglediVozila'] }) {
  const skupaj = ogledi?.skupaj ?? 0;
  return (
    <Tooltip
      label="Šteje se vsak obisk javne strani; osvežitev istega obiskovalca v 30 minutah ne."
      multiline
      w={260}
      position="top-start"
    >
      <Group gap={6} wrap="nowrap" align="flex-start" style={{ alignSelf: 'flex-start' }}>
        <IconEye size={16} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
        {skupaj === 0 ? (
          <Text size="sm" c="dimmed">
            Javne strani vozila še ni nihče odprl.
          </Text>
        ) : (
          <Text size="sm">
            Ogledi javne strani: <b>{stevilo(skupaj)}</b>
            <Text span size="sm" c="dimmed">
              {' '}
              (zadnjih 7 dni: {stevilo(ogledi?.zadnjih7Dni ?? 0)}, zadnji {datum(ogledi?.zadnji)})
            </Text>
          </Text>
        )}
      </Group>
    </Tooltip>
  );
}

/** Povpraševanja za vozilo (najnovejša najprej); klik odpre podrobnosti. */
export function PovprasevanjaVozila({ povprasevanja }: { povprasevanja: Shema['PovprasevanjePodatki'][] }) {
  return (
    <Kartica naslov="Povpraševanja">
      {povprasevanja.length === 0 ? (
        <Text size="sm" c="dimmed">
          Za to vozilo še ni povpraševanj.
        </Text>
      ) : (
        <Stack gap={6}>
          {povprasevanja.map((p) => {
            const s = stanjePovprasevanja(p.stanje);
            return (
              <Group key={p.id} justify="space-between" wrap="nowrap" gap="xs">
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                  <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
                  <Anchor component={Link} to={`/povprasevanja/${p.id}`} fw={p.stanje === 'NOVO' ? 700 : 500} truncate>
                    {p.ime}
                  </Anchor>
                </Group>
                <Text size="sm" c="dimmed" className="brez-preloma">
                  {casIzMs(p.ustvarjeno)}
                </Text>
              </Group>
            );
          })}
        </Stack>
      )}
    </Kartica>
  );
}
